import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthScene, AuthCard, AuthInput } from "@/pages/Login";

export default function Welcome() {
  const navigate = useNavigate();
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr("New password must be at least 8 characters.");
    if (next !== confirm) return setErr("New passwords don't match.");
    setBusy(true);
    try {
      await changePassword(current, next);
      navigate("/", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScene>
      <div className="fh-eyebrow" style={{ color: "#FFE066" }}>
        Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
      </div>
      <h1 className="fh-heading" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>
        Set your password
      </h1>
      <p className="fh-text" style={{ maxWidth: "46ch" }}>
        <Sparkles className="inline h-4 w-4 text-amber-300 mr-1" />
        First time signing in. Enter the temporary password you were emailed, then
        choose a new password you'll remember.
      </p>

      <AuthCard>
        <form onSubmit={submit}>
          <AuthInput
            label="Temporary password"
            type="password"
            value={current}
            onChange={setCurrent}
            icon={<Lock className="h-4 w-4" />}
            testId="welcome-current"
            autoFocus
            autoComplete="current-password"
          />
          <AuthInput
            label="New password"
            type="password"
            value={next}
            onChange={setNext}
            icon={<Lock className="h-4 w-4" />}
            testId="welcome-new"
            autoComplete="new-password"
          />
          <AuthInput
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={setConfirm}
            icon={<Lock className="h-4 w-4" />}
            testId="welcome-confirm"
            autoComplete="new-password"
          />
          {err ? (
            <div className="text-sm text-red-300 mb-3" data-testid="welcome-error">{err}</div>
          ) : null}
          <button type="submit" disabled={busy} className="fh-btn-primary w-full" data-testid="welcome-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {busy ? "Saving…" : "Save & continue"}
          </button>
          <button
            type="button"
            onClick={() => { logout(); navigate("/login", { replace: true }); }}
            className="mt-3 text-xs text-white/55 hover:text-amber-300 underline-offset-4 hover:underline"
            data-testid="welcome-signout"
          >
            Sign out instead
          </button>
        </form>
      </AuthCard>
    </AuthScene>
  );
}
